import { useState } from "react";
import { Clock3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
export function SnoozePicker({
  until,
  onSave,
}: {
  until?: string | null;
  onSave: (date: string | null) => Promise<void>;
}) {
  const { language, t } = useLanguage();
  const de = language === "de";
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(false);
  const later = new Date();
  later.setHours(later.getHours() + 3, 0, 0, 0);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  const friday = new Date();
  friday.setHours(8, 0, 0, 0);
  friday.setDate(friday.getDate() + ((5 - friday.getDay() + 7) % 7 || 7));
  const monday = new Date();
  monday.setHours(8, 0, 0, 0);
  monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7 || 7));
  const save = async (date: Date | null) => {
    if (pending) return;
    if (
      date &&
      (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now())
    ) {
      toast.error(t("support.snooze.future", "Choose a future time."));
      return;
    }
    setPending(true);
    try {
      await onSave(date?.toISOString() ?? null);
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("support.snooze.saveFailed", "Could not save.")
      );
    } finally {
      setPending(false);
    }
  };
  const format = (date: Date) =>
    new Intl.DateTimeFormat(de ? "de-DE" : "en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="support-snooze-trigger"
          aria-label={t("support.snooze.title", "Snooze")}
        >
          <Clock3 size={12} />
          {t("support.snooze.later", "Later")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 rounded-xl p-3" align="end">
        <div className="flex flex-col gap-2">
          <strong className="text-sm">
            {t("support.snooze.title", "Snooze")}
          </strong>
          <span className="text-xs text-muted-foreground">
            {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </span>
          {[
            [t("support.snooze.today", "Later today"), later],
            [t("support.snooze.tomorrow", "Tomorrow morning"), tomorrow],
            [t("support.snooze.friday", "Friday"), friday],
            [t("support.snooze.nextWeek", "Next week"), monday],
          ].map(([label, date]) => (
            <button
              key={String(label)}
              disabled={pending}
              className="flex justify-between gap-2 rounded-lg bg-[#F4F3EF] p-2 text-left text-xs"
              onClick={() => void save(date as Date)}
            >
              <strong>{label as string}</strong>
              <span>{format(date as Date)}</span>
            </button>
          ))}
          <label className="mt-1 text-xs">
            {t("support.snooze.custom", "Custom time")}
            <input
              className="mt-1 w-full rounded border p-2"
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
          <button
            className="support-primary"
            disabled={pending || !value}
            onClick={() => void save(new Date(value))}
          >
            {t("support.snooze.set", "Set")}
          </button>
          {until && (
            <button
              className="text-xs underline"
              disabled={pending}
              onClick={() => void save(null)}
            >
              {t("support.snooze.cancel", "Cancel snooze")}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
